<template>
  <v-main>
    <v-container fluid>
      <!-- Page Header Card -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <v-row align="center">
                <v-col cols="12" md="8">
                  <h3 class="text-h5 font-weight-bold primary--text mb-2">
                    <v-icon left color="primary">mdi-plus</v-icon>
                    Create New Language
                  </h3>
                  <p class="text-subtitle-1 text--secondary mb-0">
                    Add a new language to the system
                  </p>
                </v-col>
                <v-col cols="12" md="4" class="text-right">
                  <v-btn
                    text
                    :to="{ name: 'languages' }"
                    class="mr-2"
                  >
                    <v-icon left>mdi-arrow-left</v-icon>
                    Back to List
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Main Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl">
            <v-form ref="form" v-model="valid" lazy-validation>
              <v-card-text class="pa-6">
                <!-- Basic Information Section -->
                <div class="mb-6">
                  <h4 class="text-h6 font-weight-bold primary--text mb-4">
                    <v-icon left color="primary">mdi-information</v-icon>
                    Basic Information
                  </h4>
                  
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.code"
                        label="Language Code *"
                        prepend-inner-icon="mdi-code-tags"
                        solo
                        rounded
                        :rules="codeRules"
                        :error-messages="errors.code"
                        hint="ISO language code (e.g., en, hi, ta)"
                        persistent-hint
                        counter="10"
                        @input="clearFieldError('code')"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.name"
                        label="Language Name *"
                        prepend-inner-icon="mdi-translate"
                        solo
                        rounded
                        :rules="nameRules"
                        :error-messages="errors.name"
                        hint="English name of the language"
                        persistent-hint
                        counter="100"
                        @input="clearFieldError('name')"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.native_name"
                        label="Native Name"
                        prepend-inner-icon="mdi-alphabet-latin"
                        solo
                        rounded
                        :error-messages="errors.native_name"
                        hint="Language name in its own script (optional)"
                        persistent-hint
                        counter="100"
                        @input="clearFieldError('native_name')"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-switch
                        v-model="form.is_active"
                        label="Active Status"
                        color="success"
                        inset
                        hide-details
                      >
                        <template v-slot:prepend>
                          <v-icon color="success" class="mr-2">
                            {{ form.is_active ? 'mdi-check-circle' : 'mdi-close-circle' }}
                          </v-icon>
                        </template>
                      </v-switch>
                      <div class="text-caption text--secondary mt-1">
                        {{ form.is_active ? 'Language is active and available for use' : 'Language is inactive and not available' }}
                      </div>
                    </v-col>
                  </v-row>
                </div>

                <!-- Common Language Examples Section -->
                <div class="mb-6">
                  <h4 class="text-h6 font-weight-bold primary--text mb-4">
                    <v-icon left color="primary">mdi-lightbulb</v-icon>
                    Common Language Examples
                  </h4>
                  
                  <v-row>
                    <v-col cols="12">
                      <v-card outlined class="pa-4">
                        <div class="text-subtitle-2 mb-3">Quick Select Examples:</div>
                        <v-chip-group column>
                          <v-chip
                            v-for="example in languageExamples"
                            :key="example.code"
                            @click="fillExample(example)"
                            outlined
                            small
                            class="mr-2 mb-2"
                          >
                            <strong>{{ example.code }}</strong> - {{ example.name }}
                            <span v-if="example.native_name" class="ml-1">({{ example.native_name }})</span>
                          </v-chip>
                        </v-chip-group>
                        <div class="text-caption text--secondary mt-2">
                          Click on any example to auto-fill the form fields
                        </div>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </v-card-text>

              <!-- Form Actions -->
              <v-card-actions class="pa-6 pt-0">
                <v-spacer></v-spacer>
                <v-btn
                  large
                  text
                  :to="{ name: 'languages' }"
                  class="mr-2"
                >
                  Cancel
                </v-btn>
                <v-btn
                  large
                  color="primary"
                  @click="saveLanguage"
                  :loading="saving"
                  :disabled="!valid"
                  elevation="2"
                >
                  <v-icon left>mdi-content-save</v-icon>
                  Create Language
                </v-btn>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Success Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="4000" top>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-main>
</template>

<script>
import axios from 'axios'

export default {
  name: 'LanguageCreate',
  data() {
    return {
      valid: false,
      saving: false,
      form: {
        code: '',
        name: '',
        native_name: '',
        is_active: true
      },
      errors: {},
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      codeRules: [
        v => !!v || 'Language code is required',
        v => (v && v.length >= 2) || 'Language code must be at least 2 characters',
        v => (v && v.length <= 10) || 'Language code must be less than 10 characters',
        v => /^[a-z]{2,3}(-[A-Z]{2})?$/.test(v) || 'Invalid language code format (e.g., en, hi, en-US)'
      ],
      nameRules: [
        v => !!v || 'Language name is required',
        v => (v && v.length >= 2) || 'Language name must be at least 2 characters',
        v => (v && v.length <= 100) || 'Language name must be less than 100 characters'
      ],
      languageExamples: [
        { code: 'en', name: 'English', native_name: 'English' },
        { code: 'hi', name: 'Hindi', native_name: 'हिन्दी' },
        { code: 'ta', name: 'Tamil', native_name: 'தமிழ்' },
        { code: 'te', name: 'Telugu', native_name: 'తెలుగు' },
        { code: 'kn', name: 'Kannada', native_name: 'ಕನ್ನಡ' },
        { code: 'ml', name: 'Malayalam', native_name: 'മലയാളം' },
        { code: 'bn', name: 'Bengali', native_name: 'বাংলা' },
        { code: 'gu', name: 'Gujarati', native_name: 'ગુજરાતી' },
        { code: 'mr', name: 'Marathi', native_name: 'मराठी' },
        { code: 'pa', name: 'Punjabi', native_name: 'ਪੰਜਾਬੀ' },
        { code: 'ur', name: 'Urdu', native_name: 'اردو' },
        { code: 'or', name: 'Odia', native_name: 'ଓଡ଼ିଆ' },
        { code: 'as', name: 'Assamese', native_name: 'অসমীয়া' },
        { code: 'sa', name: 'Sanskrit', native_name: 'संस्कृतम्' },
        { code: 'ne', name: 'Nepali', native_name: 'नेपाली' },
        { code: 'si', name: 'Sinhala', native_name: 'සිංහල' },
        { code: 'my', name: 'Myanmar', native_name: 'မြန်မာ' },
        { code: 'th', name: 'Thai', native_name: 'ไทย' },
        { code: 'vi', name: 'Vietnamese', native_name: 'Tiếng Việt' },
        { code: 'ko', name: 'Korean', native_name: '한국어' },
        { code: 'ja', name: 'Japanese', native_name: '日本語' },
        { code: 'zh', name: 'Chinese', native_name: '中文' },
        { code: 'ar', name: 'Arabic', native_name: 'العربية' },
        { code: 'fa', name: 'Persian', native_name: 'فارسی' },
        { code: 'tr', name: 'Turkish', native_name: 'Türkçe' },
        { code: 'ru', name: 'Russian', native_name: 'Русский' },
        { code: 'de', name: 'German', native_name: 'Deutsch' },
        { code: 'fr', name: 'French', native_name: 'Français' },
        { code: 'es', name: 'Spanish', native_name: 'Español' },
        { code: 'pt', name: 'Portuguese', native_name: 'Português' },
        { code: 'it', name: 'Italian', native_name: 'Italiano' },
        { code: 'nl', name: 'Dutch', native_name: 'Nederlands' },
        { code: 'sv', name: 'Swedish', native_name: 'Svenska' },
        { code: 'no', name: 'Norwegian', native_name: 'Norsk' },
        { code: 'da', name: 'Danish', native_name: 'Dansk' },
        { code: 'fi', name: 'Finnish', native_name: 'Suomi' },
        { code: 'pl', name: 'Polish', native_name: 'Polski' },
        { code: 'cs', name: 'Czech', native_name: 'Čeština' },
        { code: 'sk', name: 'Slovak', native_name: 'Slovenčina' },
        { code: 'hu', name: 'Hungarian', native_name: 'Magyar' },
        { code: 'ro', name: 'Romanian', native_name: 'Română' },
        { code: 'bg', name: 'Bulgarian', native_name: 'Български' },
        { code: 'hr', name: 'Croatian', native_name: 'Hrvatski' },
        { code: 'sr', name: 'Serbian', native_name: 'Српски' },
        { code: 'sl', name: 'Slovenian', native_name: 'Slovenščina' },
        { code: 'et', name: 'Estonian', native_name: 'Eesti' },
        { code: 'lv', name: 'Latvian', native_name: 'Latviešu' },
        { code: 'lt', name: 'Lithuanian', native_name: 'Lietuvių' },
        { code: 'el', name: 'Greek', native_name: 'Ελληνικά' },
        { code: 'he', name: 'Hebrew', native_name: 'עברית' }
      ]
    }
  },
  methods: {
    fillExample(example) {
      this.form.code = example.code
      this.form.name = example.name
      this.form.native_name = example.native_name
      
      // Clear any existing errors
      this.errors = {}
      
      // Validate form
      this.$refs.form.validate()
    },
    clearFieldError(field) {
      if (this.errors[field]) {
        this.$delete(this.errors, field)
      }
    },
    async saveLanguage() {
      if (!this.$refs.form.validate()) {
        this.showSnackbar('Please correct the errors in the form', 'error')
        return
      }

      this.saving = true
      this.errors = {}

      try {
        const response = await axios.post('/api/languages', this.form)
        
        if (response.data.success) {
          this.showSnackbar('Language created successfully', 'success')
          
          // Navigate to the languages list after a short delay
          setTimeout(() => {
            this.$router.push({ name: 'languages' })
          }, 1000)
        } else {
          this.showSnackbar(response.data.message || 'Failed to create language', 'error')
        }
      } catch (error) {
        console.error('Error creating language:', error)
        
        if (error.response && error.response.data && error.response.data.errors) {
          this.errors = error.response.data.errors
          this.showSnackbar('Please correct the validation errors', 'error')
        } else {
          this.showSnackbar('Error creating language', 'error')
        }
      } finally {
        this.saving = false
      }
    },
    showSnackbar(text, color = 'success') {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    }
  }
}
</script>

<style scoped>
.v-card {
  transition: all 0.3s ease;
}

.v-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  cursor: pointer;
}

.text-h6 {
  border-bottom: 2px solid #e0e0e0;
  padding-bottom: 8px;
}

.v-text-field >>> .v-input__control .v-input__slot {
  min-height: 48px;
}

.v-switch >>> .v-input__control {
  width: auto;
}
</style>